<template>
	<v-card v-if="!isTabView" flat class="w-100 page">
		<v-card-title class="d-flex flex-column align-start px-8">
			<div class="checklist-editor-header checklist-editor-content w-100 mx-auto mt-4">
				<div>
					<v-btn class="px-0 mb-1" color="primary" variant="text" @click="goBack()">
						<v-icon start>icon-arrow-left</v-icon>All checklists
					</v-btn>
					<h5>{{ isInEditMode ? "Edit Checklist" : "New Checklist" }}</h5>
				</div>
				<div class="d-flex align-center ga-2">
					<v-btn rounded="pill" variant="outlined" @click="goBack()">Back</v-btn>
					<v-btn v-if="isInEditMode" rounded="pill" variant="tonal" color="primary" :loading="saving" @click="create()">
						Save As New
					</v-btn>
					<v-btn rounded="pill" variant="flat" color="primary" :loading="saving" @click="isInEditMode ? update() : create()">
						Save
					</v-btn>
				</div>
			</div>
			<v-label v-if="isInEditMode" class="header-label checklist-editor-content w-100 mx-auto mt-3 text-wrap">
				Please note that changes to this checklist template will only apply to new checklists, not ones that have already been added
				to a project.
			</v-label>
		</v-card-title>

		<v-card-text class="px-8">
			<v-form ref="form" v-model="formValid" class="checklist-editor-content w-100 mx-auto">
				<div class="mb-6">
					<!-- LSP-16482: one persistent external label replaces the repeated heading/floating-label copy. -->
					<!-- hide-details stays false (never "auto"): "auto" only renders the message row once a
					     message exists, so the first validation error inserts ~28px and shifts the Add New
					     Checklist Item button down out from under an in-flight click (LSP-15994). The row must
					     be reserved from the first render, and must not depend on :counter incidentally
					     reserving it via Vuetify's details slot. -->
					<label class="form-label" for="checklist-name">Checklist name <span class="text-error">*</span></label>
					<v-text-field
						id="checklist-name"
						v-test="'checklist-name-input'"
						v-model="checklist.name"
						variant="outlined"
						density="compact"
						:counter="FIELD_LENGTHS.TEXT"
						:counter-value="countCharactersTrimmed"
						:rules="nameRules"
						:hide-details="false"
						:autofocus="true"
						placeholder="Checklist name (Required)"
						@keydown.enter="handleNameEnter" />
				</div>

				<div>
					<h6>Checklist items</h6>
					<v-btn
						class="mt-4"
						rounded="pill"
						elevation="0"
						variant="flat"
						color="primary"
						@click="addNewItem">
						<v-icon start>icon-plus</v-icon>Add checklist item
					</v-btn>

					<div v-if="checklistItems.length" class="checklist-items-table my-4">
						<div class="checklist-items-header">
							<span>#</span><span>Checklist item title</span><span>Require photo or video</span><span>Reorder</span
							><span>Remove</span>
						</div>
						<div
							v-for="(item, index) in checklistItems"
							:key="item.id"
							class="checklist-item py-3"
							:draggable="true"
							@dragstart="onDragStart(index)"
							@dragover.prevent="onDragOver(index)"
							@drop="onDrop(index)"
							@dragend="onDragEnd">
							<!-- Drop indicator line (above — when dragging up) -->
							<div
								v-if="dropIndicatorIndex === index && draggedIndex !== -1 && draggedIndex > index"
								class="drop-indicator mb-3"></div>

							<div class="checklist-item-row">
								<div class="item-number-wrap">
									<v-tooltip location="bottom">
										<template #activator="{ props }">
											<v-icon
												class="drag-icon min-h-10"
												v-bind="props"
												@touchstart="(event) => dragStart(index, event)"
												@touchmove="(event) => dragMove(event)"
												@touchend="(event) => dragEnd(event)"
												@touchcancel="cancelTouchDrag"
												>icon-grid-2x3</v-icon
											>
										</template>
										<span>Drag to reorder</span>
									</v-tooltip>
									<span class="item-number">{{ item.order }}</span>
								</div>

								<div class="checklist-item-fields flex-grow-1 flex-shrink-1">
									<v-text-field
										:ref="`itemField-${index}`"
										v-test="`checklist-item-title-input-${index}`"
										v-model="item.name"
										variant="outlined"
										density="compact"
										class="mb-0"
										:counter="FIELD_LENGTHS.TEXT"
										:counter-value="countCharactersTrimmed"
										:rules="itemRules"
										:hide-details="false"
										:aria-label="`Checklist item title ${index + 1}`"
										placeholder="Checklist item title (Required)"
										@keydown.enter.prevent="handleItemEnter(index)" />
								</div>
								<div class="item-actions">
									<div class="media-action">
										<span class="mobile-media-label">Require photo or video</span>
										<v-checkbox
											v-model="item.requiresMedia"
											class="media-checkbox"
											color="primary"
											hide-details
											true-icon="icon-check-heavy"
											false-icon="icon-checkbox-unmarked-stroke"
											:aria-label="`Require photo or video for item ${index + 1}`" />
									</div>
									<div class="reorder-actions">
										<v-btn class="reorder-button" rounded="lg" variant="outlined" density="comfortable" :disabled="index === 0" :aria-label="`Move item up ${index + 1}`" @click="moveItem(index, -1)"><v-icon>icon-chevron-up</v-icon></v-btn>
										<v-btn class="reorder-button" rounded="lg" variant="outlined" density="comfortable" :disabled="index === checklistItems.length - 1" :aria-label="`Move item down ${index + 1}`" @click="moveItem(index, 1)"><v-icon>icon-chevron-down</v-icon></v-btn>
									</div>

									<v-btn
										class="remove-item-button"
										color="error"
										rounded="lg"
										density="comfortable"
										variant="outlined"
										:aria-label="`Remove item ${index + 1}`"
										@click="removeItem(index)">
										<v-icon>icon-trash-full-stroke</v-icon>
									</v-btn>
								</div>
							</div>

							<!-- Drop indicator line (below — when dragging down) -->
							<div
								v-if="dropIndicatorIndex === index && draggedIndex !== -1 && draggedIndex < index"
								class="drop-indicator mt-3"></div>
						</div>
					</div>

					<v-btn
						v-if="checklistItems.length"
						rounded="pill"
						elevation="0"
						variant="flat"
						color="primary"
						@click="addNewItem">
						<v-icon start>icon-plus</v-icon>Add checklist item
					</v-btn>
					<div v-if="checklistItems.length >= maxChecklistItems" class="text-caption mt-2">
						You can add up to {{ maxChecklistItems }} items per checklist.
					</div>
				</div>
			</v-form>
		</v-card-text>
	</v-card>

	<div v-else>
		<v-form ref="form" v-model="formValid">
			<div class="d-flex align-center justify-space-between w-100 my-4">
				<!-- LSP-16482: the row keeps its action buttons; the field names itself through
				     FieldLabel below, so no heading repeats "Checklist Name" here either. -->
				<span></span>
				<div class="d-flex align-center ga-2">
					<v-btn rounded="pill" elevation="0" @click.stop="handleCancel()"> Cancel </v-btn>
					<v-btn rounded="pill" elevation="0" color="primary" :loading="saving" @click.stop="create()"> Save </v-btn>
				</div>
			</div>
			<!-- Reserved message row, same as the card view above (LSP-15994). -->
			<v-text-field
				v-test="'checklist-name-input'"
				v-model="checklist.name"
				variant="outlined"
				density="compact"
				:counter="FIELD_LENGTHS.TEXT"
				:counter-value="countCharactersTrimmed"
				:rules="nameRules"
				:hide-details="false"
				:autofocus="true"
				@keydown.enter="handleNameEnter">
				<template #label><FieldLabel label="Checklist Name (Required)" /></template>
			</v-text-field>
			<div>
				<h6>Checklist Items</h6>

				<div class="mb-4">
					<div
						v-for="(item, index) in checklistItems"
						:key="item.id"
						class="checklist-item py-3"
						:draggable="true"
						@dragstart="onDragStart(index)"
						@dragover.prevent="onDragOver(index)"
						@drop="onDrop(index)"
						@dragend="onDragEnd">
						<!-- Drop indicator line (above — when dragging up) -->
						<div
							v-if="dropIndicatorIndex === index && draggedIndex !== -1 && draggedIndex > index"
							class="drop-indicator mb-3"></div>

						<div class="d-flex align-start justify-start">
							<v-tooltip location="bottom">
								<template #activator="{ props }">
									<v-icon
										class="drag-icon min-h-10"
										v-bind="props"
										@touchstart="(event) => dragStart(index, event)"
										@touchmove="(event) => dragMove(event)"
										@touchend="(event) => dragEnd(event)"
										@touchcancel="cancelTouchDrag"
										>icon-grid-2x3</v-icon
									>
								</template>
								<span>Drag to reorder</span>
							</v-tooltip>
							<div class="item-number min-h-10">
								{{ item.order }}
							</div>

							<div class="checklist-item-fields flex-grow-1 flex-shrink-1">
								<v-text-field
									:ref="`itemField-${index}`"
									v-test="`checklist-item-title-input-${index}`"
									v-model="item.name"
									variant="outlined"
									density="compact"
									class="mb-0 ml-2 min-h-10"
									:counter="FIELD_LENGTHS.TEXT"
									:counter-value="countCharactersTrimmed"
									:rules="itemRules"
									:hide-details="false"
									:aria-label="`Checklist item title ${index + 1}`"
									@keydown.enter.prevent="handleItemEnter(index)">
									<template #label><FieldLabel :label="'Checklist Item Title (Required)'" /></template>
								</v-text-field>
								<v-switch v-model="item.requiresMedia" class="ml-2" label="Require a photo or video"></v-switch>
							</div>

							<v-btn icon class="min-h-10 ml-1" color="error" density="compact" variant="plain" @click="removeItem(index)">
								<v-icon>icon-trash-full-stroke</v-icon>
							</v-btn>
						</div>

						<!-- Drop indicator line (below — when dragging down) -->
						<div
							v-if="dropIndicatorIndex === index && draggedIndex !== -1 && draggedIndex < index"
							class="drop-indicator mt-3"></div>
					</div>
				</div>

				<v-btn rounded="pill" elevation="0" variant="flat" color="primary" @click="addNewItem">
					<v-icon start>icon-plus</v-icon>Add checklist item
				</v-btn>
				<div v-if="checklistItems.length >= maxChecklistItems" class="text-caption mt-2">
					You can add up to {{ maxChecklistItems }} items per checklist.
				</div>
			</div>
		</v-form>
	</div>
</template>

<script lang="ts">
	import type { PropType } from "vue";
	import Checklist from "@/types/Checklist";
	import ChecklistItem from "@/types/ChecklistItem";
	import api from "@/api";
	import dirtyCheck from "../helpers/dirty";
	import { calculateTimeout, getMessageFromError, countCharactersTrimmed } from "@/helpers/utilities";
	import { logError, logWarn } from "@/helpers/logError";
	import { FIELDS_NEED_ATTENTION, couldNotFind, couldNotLoad, isNotFoundError } from "@/helpers/userMessages";
	import { useToast } from "vue-toastification";
	import { VuetifyValidation, FIELD_LENGTHS } from "@/helpers/Validation";
	import {
		confirmDiscardChanges,
		INVALID_CHANGES_DIALOG_TEXT,
		UNSAVED_CHANGES_DIALOG_OPTIONS,
		UNSAVED_CHANGES_DIALOG_TEXT
	} from "@/composables/useUnsavedChangesGuard";
	import { MAX_CHECKLIST_ITEMS } from "@/constants/checklist";
	import type { VForm } from "vuetify/components";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";

	interface SaveOptions {
		redirectAfterDone?: boolean;
	}

	const TOUCH_DRAG_ACTIVATION_DELAY_MS = 300;
	const TOUCH_DRAG_MOVEMENT_THRESHOLD_PX = 10;
	const CHECKLIST_ITEMS_REQUIRED_DIALOG_TEXT = {
		...INVALID_CHANGES_DIALOG_TEXT,
		// Keeps the shared prompt's voice (LSP-16869): the blocker, then the same question.
		message: "A checklist needs at least one item before we can save. Would you like to leave without saving?"
	};

	export default {
		name: "ChecklistEditor",
		components: {
			FieldLabel
		},
		setup() {
			const toast = useToast();
			return { toast, FIELD_LENGTHS, countCharactersTrimmed };
		},
		async beforeRouteLeave() {
			if (!this.dirty) {
				return;
			}
			const hasChecklistItems = this.checklistItems.length > 0;
			let formValid = false;
			try {
				// LSP-16482: passesValidationRules(), never validateForm(). Leaving is not a save
				// attempt, so it must not paint the editor red — see the method's comment.
				formValid = hasChecklistItems && this.passesValidationRules();
			} catch (error) {
				logError("ChecklistEditor.beforeRouteLeave: validation threw — blocking navigation", error);
				this.toast.error("We couldn't validate your changes. Please try again before leaving.");
				return false;
			}
			if (!formValid) {
				let discard = false;
				try {
					discard =
						(await confirmDiscardChanges(
							this,
							hasChecklistItems ? INVALID_CHANGES_DIALOG_TEXT : CHECKLIST_ITEMS_REQUIRED_DIALOG_TEXT
						)) === true;
				} catch {
					return false;
				}
				return discard ? undefined : false;
			}
			const result = await (this as any).$root.$confirm.open(
				UNSAVED_CHANGES_DIALOG_TEXT.title,
				UNSAVED_CHANGES_DIALOG_TEXT.message,
				false,
				UNSAVED_CHANGES_DIALOG_TEXT.agreeText,
				UNSAVED_CHANGES_DIALOG_TEXT.cancelText,
				false,
				false,
				UNSAVED_CHANGES_DIALOG_OPTIONS
			);
			if (result === null) {
				return false;
			}
			if (result) {
				let saved;
				try {
					if (this.isInEditMode) {
						saved = await this.update({ redirectAfterDone: false });
					} else {
						saved = await this.create({ redirectAfterDone: false });
					}
				} catch (error) {
					// Defense: save threw unexpectedly. Block navigation so the user sees the error.
					logError("ChecklistEditor.beforeRouteLeave: save threw — blocking navigation so user can see the error", error, {
						mode: this.isInEditMode ? "update" : "create"
					});
					return false;
				}
				// LSP-16279: create()/update() catch their own errors and show a toast rather than
				// throwing, returning false when nothing was persisted (offline/server error, invalid
				// fields). Block navigation in that case so the user stays and sees the toast.
				if (saved === false) return false;
			}
		},
		props: {
			// TabView is when the checklist editor is used in the project page
			isTabView: {
				type: Boolean,
				default: false
			},
			saveToProject: {
				type: Function as PropType<(checklistId: string) => Promise<boolean>>,
				default: undefined
			},
			checklistName: {
				type: String,
				default: ""
			},
			existingNames: {
				type: Array as () => string[],
				default: () => []
			}
		},
		data() {
			return {
				checklist: {
					name: "",
					items: [] as Partial<ChecklistItem>[]
				} as Partial<Checklist>,
				checklistItems: [] as Partial<ChecklistItem>[],
				saving: false,
				// LSP-19790: id of the currently-visible save-failure banner, so a repeated Save click
				// replaces it instead of stacking a second copy. Covers EVERY way a save can be refused,
				// not just the client-side name check: the server rejects duplicate checklist ITEM names
				// too ("Duplicate checklist item name \"b\" found." — contact-api checklists.ts), and that
				// arrives through the generic catch below, which is the one the tester actually hit.
				// Same pattern as MediaLightBox's `lastSaveToastId`. Dismissing an id that already
				// expired is a no-op in vue-toastification, so a stale value here is harmless.
				saveErrorToastId: null as number | string | null,
				draggedIndex: -1,
				dropIndicatorIndex: -1,
				// Dragover/touchmove can fire repeatedly in one frame. Keep only the
				// latest target and update the indicator at most once per frame.
				pendingDropIndicatorIndex: null as number | null,
				dropIndicatorFrame: null as number | null,
				pendingTouchDragIndex: null as number | null,
				touchDragStartPosition: null as { clientX: number; clientY: number } | null,
				touchDragActivationTimer: null as ReturnType<typeof setTimeout> | null,
				nameRules: [
					VuetifyValidation.RequiredValidationField("Checklist Name is required.").isValid,
					VuetifyValidation.MaxLengthValidation(
						FIELD_LENGTHS.TEXT,
						`Checklist Name must not exceed ${FIELD_LENGTHS.TEXT} characters.`
					).isValid
				],
				itemRules: [
					VuetifyValidation.RequiredValidationField("Please enter a title for this checklist item.").isValid,
					VuetifyValidation.MaxLengthValidation(
						FIELD_LENGTHS.TEXT,
						`Checklist Item Title must not exceed ${FIELD_LENGTHS.TEXT} characters.`
					).isValid
				],
				isInEditMode: false,
				dirty: false,
				sourceChecklist: {
					name: "",
					items: [] as Partial<ChecklistItem>[]
				} as Partial<Checklist>,
				formValid: false,
				settingsPageUrl: "",
				maxChecklistItems: MAX_CHECKLIST_ITEMS,
				isUnmounted: false
			};
		},
		watch: {
			checklist: {
				handler() {
					this.checkDirty();
				},
				deep: true
			},
			checklistItems: {
				handler() {
					this.checkDirty();
				},
				deep: true
			}
		},
		async mounted(): Promise<void> {
			if (this.$route.params.id !== "new" && !this.isTabView) {
				// LSP-21868: a failed load must not fall through to an empty "New Checklist" form that saves as a
				// new template. Say what happened — plainly for a deleted checklist — and return to the list,
				// like InstructionEditor does.
				const checklistId = this.$route.params.id;
				try {
					await this.loadChecklist();
				} catch (error) {
					logError("ChecklistEditor.mounted: failed to load checklist", error, { checklistId });
					// The user already left: a late failure must not toast over, or navigate away from, where they are now.
					if (this.isUnmounted) {
						logWarn("ChecklistEditor.mounted: load failed after the editor closed; skipping toast and redirect", {
							checklistId
						});
						return;
					}
					// Yellow warning toast, never the blue error toast. replace, not push: Back must not reopen the dead link.
					const message = isNotFoundError(error) ? couldNotFind("checklist") : couldNotLoad("this checklist");
					this.toast.warning(message, { timeout: calculateTimeout(message) });
					this.$router.replace("/settings/checklist-templates");
					return;
				}
				this.isInEditMode = true;
			} else {
				if (this.isTabView) {
					if (this.checklistName) {
						this.checklist.name = this.checklistName;
					}
				}
				this.sourceChecklist = JSON.parse(JSON.stringify(this.checklist));
			}
			const me = await api.getMe();
			// Settings is no longer org-id-scoped in the URL — the page reads
			// org from the token. We still gate the link on having an org so
			// users without one don't see a broken "Back to settings" target.
			if (me && me.organization) {
				this.settingsPageUrl = "/settings";
			}
		},
		beforeUnmount(): void {
			this.$emit("update:isDirty", false);
			this.isUnmounted = true;
			this.cancelDropIndicatorUpdate();
			this.cancelTouchDrag();
		},
		methods: {
			async validateForm(): Promise<boolean> {
				const form = this.$refs.form as VForm | null;
				const validation = await form?.validate();
				return validation?.valid ?? false;
			},

			/**
			 * LSP-16482: whether the form WOULD pass, evaluated without displaying anything.
			 *
			 * `VForm.validate()` renders every field's error message regardless of the global
			 * `validateOn: "blur"` (plugins/vuetify.js) — that is what makes Save show the user
			 * everything that is wrong, and it is correct there. It is wrong on the way out: calling
			 * it from beforeRouteLeave turned every untouched blank item row red for someone who had
			 * only clicked Back, so the editor shouted at them about a save they never attempted.
			 *
			 * These are the same rule arrays bound to the fields, so there is one source of truth for
			 * what "valid" means and no chance of the predicate drifting from what Save enforces.
			 */
			passesValidationRules(): boolean {
				const passes = (rules: ((value: unknown) => boolean | string)[], value: unknown) =>
					rules.every((rule) => rule(value) === true);
				return (
					passes(this.nameRules, this.checklist.name) && this.checklistItems.every((item) => passes(this.itemRules, item.name))
				);
			},

			createNewItem(): Partial<ChecklistItem> {
				return {
					name: "",
					order: 0,
					requiresMedia: false
				};
			},

			sortItemsByOrder(items: any[]): any[] {
				return items ? [...items].sort((a: any, b: any) => a.order - b.order) : [];
			},

			deepCopyAndSortItems(items: any[]): any[] {
				return items ? items.map((item: any) => ({ ...item })).sort((a: any, b: any) => a.order - b.order) : [];
			},

			handleCancel(): void {
				this.$emit("cancel");
				this.$emit("update:isDirty", false);
			},

			goBack(): void {
				this.$router.push(`${this.settingsPageUrl}/checklist-templates`);
			},

			checkDirty(): void {
				let isDirty = false;
				isDirty = isDirty || dirtyCheck.compare(this.checklist, this.sourceChecklist);
				isDirty = isDirty || dirtyCheck.compareExactly(this.checklistItems, this.sourceChecklist.items);
				this.dirty = isDirty;
				this.$emit("update:isDirty", isDirty);
			},

			async loadChecklist(): Promise<void> {
				const checklist = await api.getChecklist(this.$route.params.id as string);
				this.checklist = checklist;

				this.checklistItems = this.sortItemsByOrder(checklist.items);

				this.sourceChecklist = {
					...checklist,
					items: this.deepCopyAndSortItems(checklist.items)
				};

				this.updateItemOrders();
			},

			addNewItem(): void {
				if (this.checklistItems.length >= this.maxChecklistItems) {
					return;
				}

				this.checklistItems.push(this.createNewItem());
				this.updateItemOrders();
				const newItemIndex = this.checklistItems.length - 1;
				this.$nextTick(() => this.focusItem(newItemIndex));
			},

			handleNameEnter(): void {
				// Check if there's a first item, if not create one
				if (this.checklistItems.length === 0) {
					this.addNewItem();
				}
				this.$nextTick(() => this.focusItem(0));
			},

			handleItemEnter(index: number): void {
				// Check if there's an item after this one
				if (index + 1 < this.checklistItems.length) {
					// Focus the next existing item
					this.focusItem(index + 1);
				} else {
					if (this.checklistItems.length >= this.maxChecklistItems) {
						return;
					}

					// Create new item after current one
					this.checklistItems.splice(index + 1, 0, this.createNewItem());
					this.updateItemOrders();

					// Focus the new item
					this.$nextTick(() => this.focusItem(index + 1));
				}
			},

			focusItem(index: number): void {
				const field = this.$refs[`itemField-${index}`] as any;

				if (field) {
					const component = Array.isArray(field) ? field[0] : field;
					const input = component?.$el?.querySelector("input");

					if (input) {
						input.focus();
					}
				}
			},

			async removeItem(index: number): Promise<void> {
				const item = this.checklistItems[index];
				const hasText = item?.name && item.name.trim().length > 0;

				if (hasText) {
					const confirmed = await (this as any).$root.$confirm.open(
						"Delete Checklist Item",
						"Are you sure you want to delete this checklist item?",
						false,
						"Delete",
						"Cancel",
						true
					);
					if (!confirmed) {
						return;
					}
				}

				this.checklistItems.splice(index, 1);
				this.updateItemOrders();
			},

			updateItemOrders(): void {
				this.checklistItems.forEach((item, index) => {
					item.order = index + 1;
				});
			},

			moveItem(index: number, direction: -1 | 1): void {
				const destinationIndex = index + direction;
				if (destinationIndex < 0 || destinationIndex >= this.checklistItems.length) return;
				const [item] = this.checklistItems.splice(index, 1);
				this.checklistItems.splice(destinationIndex, 0, item);
				this.updateItemOrders();
				this.$nextTick(() => this.focusItem(destinationIndex));
			},

			onDragStart(index: number): void {
				this.cancelDropIndicatorUpdate();
				this.draggedIndex = index;
			},

			onDragOver(index: number): void {
				this.pendingDropIndicatorIndex = index;
				if (this.dropIndicatorFrame !== null) return;
				const scheduledFrame = requestAnimationFrame(() => {
					// Ignore a callback that was cancelled or belongs to an older drag.
					if (this.dropIndicatorFrame !== scheduledFrame) return;
					this.dropIndicatorFrame = null;
					const pendingIndex = this.pendingDropIndicatorIndex;
					this.pendingDropIndicatorIndex = null;
					if (pendingIndex === null || this.draggedIndex === -1) return;
					// LSP-18065: skip the reactive write when the target is unchanged.
					if (this.draggedIndex !== pendingIndex && this.dropIndicatorIndex !== pendingIndex) {
						this.dropIndicatorIndex = pendingIndex;
					}
				});
				this.dropIndicatorFrame = scheduledFrame;
			},

			onDrop(dropIndex: number): void {
				this.flushDropIndicatorUpdate();
				if (this.draggedIndex === -1 || this.draggedIndex === dropIndex) {
					this.dropIndicatorIndex = -1;
					return;
				}
				const draggedItem = this.checklistItems.splice(this.draggedIndex, 1)[0];
				this.checklistItems.splice(dropIndex, 0, draggedItem);
				this.updateItemOrders();

				// Reset drag state
				this.draggedIndex = -1;
				this.dropIndicatorIndex = -1;
			},

			onDragEnd(): void {
				this.cancelDropIndicatorUpdate();
				this.draggedIndex = -1;
				this.dropIndicatorIndex = -1;
			},

			cancelDropIndicatorUpdate(): void {
				if (this.dropIndicatorFrame !== null) {
					cancelAnimationFrame(this.dropIndicatorFrame);
					this.dropIndicatorFrame = null;
				}
				this.pendingDropIndicatorIndex = null;
			},

			flushDropIndicatorUpdate(): void {
				const pendingIndex = this.pendingDropIndicatorIndex;
				this.cancelDropIndicatorUpdate();
				if (pendingIndex === null || this.draggedIndex === -1 || this.draggedIndex === pendingIndex) return;
				this.dropIndicatorIndex = pendingIndex;
			},

			// Touch handlers for mobile drag functionality
			dragStart(index: number, event: TouchEvent): void {
				this.cancelTouchDrag();
				const touch = event.touches[0];
				if (!touch) return;

				this.pendingTouchDragIndex = index;
				this.touchDragStartPosition = { clientX: touch.clientX, clientY: touch.clientY };
				this.touchDragActivationTimer = setTimeout(() => {
					if (this.pendingTouchDragIndex === null) return;
					this.draggedIndex = this.pendingTouchDragIndex;
					this.touchDragActivationTimer = null;
				}, TOUCH_DRAG_ACTIVATION_DELAY_MS);
			},

			dragMove(event: TouchEvent): void {
				if (this.draggedIndex === -1) {
					const touch = event.touches[0];
					if (!touch || !this.touchDragStartPosition) {
						this.cancelTouchDrag();
						return;
					}

					const horizontalMovement = touch.clientX - this.touchDragStartPosition.clientX;
					const verticalMovement = touch.clientY - this.touchDragStartPosition.clientY;
					if (Math.hypot(horizontalMovement, verticalMovement) > TOUCH_DRAG_MOVEMENT_THRESHOLD_PX) {
						this.cancelTouchDrag();
					}
					return;
				}

				const touch = event.touches[0];
				if (!touch) return;

				event.preventDefault();
				const element = document.elementFromPoint(touch.clientX, touch.clientY);

				if (element) {
					// Find the closest checklist item element
					const checklistItem = element.closest(".checklist-item");
					if (checklistItem) {
						// Get the index from the element's position in the list
						const items = document.querySelectorAll(".checklist-item");
						const index = Array.from(items).indexOf(checklistItem);

						if (index !== -1 && index !== this.draggedIndex) {
							this.onDragOver(index);
						}
					}
				}
			},

			dragEnd(event: TouchEvent): void {
				if (this.draggedIndex === -1) {
					this.cancelTouchDrag();
					return;
				}

				event.preventDefault();
				const touch = event.changedTouches[0];
				const element = document.elementFromPoint(touch.clientX, touch.clientY);

				if (element) {
					// Find the closest checklist item element
					const checklistItem = element.closest(".checklist-item");
					if (checklistItem) {
						// Get the index from the element's position in the list
						const items = document.querySelectorAll(".checklist-item");
						const dropIndex = Array.from(items).indexOf(checklistItem);

						if (dropIndex !== -1 && dropIndex !== this.draggedIndex) {
							const draggedItem = this.checklistItems.splice(this.draggedIndex, 1)[0];
							this.checklistItems.splice(dropIndex, 0, draggedItem);
							this.updateItemOrders();
						}
					}
				}

				this.cancelTouchDrag();
			},

			cancelTouchDrag(): void {
				this.cancelDropIndicatorUpdate();
				if (this.touchDragActivationTimer !== null) {
					clearTimeout(this.touchDragActivationTimer);
				}
				this.touchDragActivationTimer = null;
				this.draggedIndex = -1;
				this.dropIndicatorIndex = -1;
				this.pendingTouchDragIndex = null;
				this.touchDragStartPosition = null;
			},

			dismissSaveErrorToast() {
				if (this.saveErrorToastId == null) return;
				this.toast.dismiss(this.saveErrorToastId);
				this.saveErrorToastId = null;
			},

			/** Show a save failure as the ONLY save-failure banner on screen. */
			showSaveErrorToast(message: string) {
				this.dismissSaveErrorToast();
				this.saveErrorToastId = this.toast.error(message);
			},

			showInvalidFormError(mode: "create" | "update", source: "current-screen" | "save-before-leave"): void {
				const checklistId = this.checklist.id ?? "new";
				const nameLength = countCharactersTrimmed(this.checklist.name ?? "");
				console.warn(
					`[ChecklistEditor] save requested but form invalid; source=${source}, checklistId=${checklistId}, mode=${mode}, nameLength=${nameLength}, itemCount=${this.checklistItems.length}`
				);
				this.showSaveErrorToast(FIELDS_NEED_ATTENTION);
			},

			async create(options: SaveOptions = {}): Promise<boolean> {
				if (this.saving) return false;
				const { redirectAfterDone = true } = options;
				if (!(await this.validateForm())) {
					this.showInvalidFormError("create", redirectAfterDone ? "current-screen" : "save-before-leave");
					// LSP-16279: invalid — not saved; signal so the guard blocks navigation.
					return false;
				}
				const name = this.checklist.name?.trim();
				if (name && this.existingNames.includes(name)) {
					// LSP-19790: one banner at a time. Every click used to push another toast, so a user
					// clicking Save a few times ended up with a stack of identical banners. Replacing the
					// previous one also restarts its timeout, so the message stays readable for the click
					// that actually produced it.
					this.showSaveErrorToast(`A checklist with the name "${name}" already exists.`);
					return false;
				}
				// Past the name check: any standing banner describes an attempt the user has since
				// changed. Clear it now so the request that follows starts from a clean slate — if it
				// fails, its own catch posts the replacement.
				this.dismissSaveErrorToast();
				if (this.saving) return false;
				this.saving = true;
				try {
					const checklistData = {
						...this.checklist,
						isTemplate: true,
						items: this.checklistItems
					};

					// Retry a failed project attachment using the template already created.
					const checklist =
						this.saveToProject && this.checklist.id
							? await api.updateChecklist(this.checklist.id, checklistData)
							: await api.createChecklist(checklistData);

					this.checklist = checklist;
					if (this.saveToProject && !(await this.saveToProject(checklist.id))) return false;

					this.checklistItems = this.sortItemsByOrder(checklist.items);

					this.sourceChecklist = {
						...checklist,
						items: this.deepCopyAndSortItems(this.checklistItems)
					};

					this.toast.success("Checklist Template saved successfully.");

					await this.$nextTick();

					this.$emit("update:isDirty", false);

					if (!this.isTabView) {
						if (redirectAfterDone) {
							this.goBack();
						}
					} else if (!this.saveToProject) {
						this.$emit("agree", checklist.id);
					}
					return true;
				} catch (error) {
					const errorMessage = getMessageFromError(error);
					this.showSaveErrorToast(errorMessage);
					console.error(error, {
						extra: {
							file: "ChecklistEditor.vue",
							method: "saveChecklist"
						},
						tags: {
							checklistId: this.$route.params.id
						}
					});
					// LSP-16279: create failed — not saved; block navigation (finally still resets flags).
					return false;
				} finally {
					this.saving = false;
				}
			},

			async update(options: SaveOptions = {}): Promise<boolean> {
				const { redirectAfterDone = true } = options;
				if (!this.checklist.id) {
					// LSP-16279: nothing to update — treat as not-saved so the guard blocks navigation.
					return false;
				}
				if (!this.dirty) {
					this.toast.info("No changes to save.");
					return false;
				}

				if (!(await this.validateForm())) {
					this.showInvalidFormError("update", redirectAfterDone ? "current-screen" : "save-before-leave");
					// LSP-16279: invalid — not saved; signal so the guard blocks navigation.
					return false;
				}

				this.saving = true;

				try {
					const checklistData = {
						...this.checklist,
						items: this.checklistItems
					};

					const checklist = await api.updateChecklist(this.checklist.id, checklistData);

					this.checklist = checklist;

					this.checklistItems = this.sortItemsByOrder(checklist.items);

					this.sourceChecklist = {
						...checklist,
						items: this.deepCopyAndSortItems(this.checklistItems)
					};

					this.toast.success("Checklist Template updated successfully.");

					await this.$nextTick();
					this.$emit("update:isDirty", false);

					if (!this.isTabView && redirectAfterDone) {
						this.goBack();
					}
					return true;
				} catch (error) {
					const errorMessage = getMessageFromError(error);
					this.showSaveErrorToast(errorMessage);
					console.error(error, {
						extra: {
							file: "ChecklistEditor.vue",
							method: "updateChecklist"
						},
						tags: {
							checklistId: this.checklist.id
						}
					});
					// LSP-16279: update failed — not saved; block navigation (finally still resets the flag).
					return false;
				} finally {
					this.saving = false;
				}
			}
		}
	};
</script>

<style scoped>
	.checklist-editor-header {
		display: flex;
		align-items: flex-end;
		justify-content: space-between;
		gap: 1rem;
	}

	.checklist-editor-content {
		max-width: 72rem;
	}

	.form-label {
		display: block;
		font-weight: 600;
		margin-bottom: 0.5rem;
	}

	.checklist-items-table {
		border: 1px solid #d9e0eb;
		border-radius: 1rem;
		overflow: hidden;
	}

	.checklist-items-header,
	.checklist-item-row {
		display: grid;
		grid-template-columns: 5rem minmax(16rem, 1fr) 12rem 8.5rem 5.5rem;
		align-items: center;
		column-gap: 1rem;
	}

	.checklist-items-header {
		min-height: 4rem;
		padding: 0 1rem;
		background: #eef3ff;
		font-weight: 600;
	}

	.checklist-items-header span:nth-child(n + 3) { text-align: center; }
	.checklist-items-header span:first-child { text-align: center; }

	.checklist-item { border-top: 1px solid #d9e0eb; }

	.checklist-item-row { padding: 1rem; }

	.item-number-wrap,
	.reorder-actions {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 0.25rem;
	}

	.item-number-wrap {
		flex-direction: column-reverse;
	}

	.reorder-button {
		width: 2.75rem;
		min-width: 2.75rem;
		height: 2.75rem;
	}

	.item-actions {
		display: contents;
	}

	.media-action {
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.mobile-media-label {
		display: none;
	}

	.media-checkbox,
	.remove-item-button { justify-self: center; }

	.media-checkbox :deep(.v-selection-control__input) {
		width: 2.25rem;
		height: 2.25rem;
		border-radius: 0.625rem;
		font-size: 1.625rem;
	}

	.media-checkbox :deep(.v-selection-control--dirty .v-selection-control__input) {
		color: white;
		background: rgb(var(--v-theme-primary));
	}

	.remove-item-button {
		width: 2.75rem;
		min-width: 2.75rem;
		height: 2.75rem;
	}

	.drag-icon::before {
		margin-right: 0px !important;
		margin-left: 0px !important;
	}

	.drag-icon:hover {
		cursor: grab;
	}

	.drop-indicator {
		height: 2px;
		background-color: #0070ff;
	}

	.checklist-item-fields {
		min-width: 0;
		width: auto;
	}

	.item-number {
		align-content: center;
		font-weight: 700;
	}

	.header-label {
		font-size: 0.875rem;
	}

	@media (max-width: 960px) {
		.checklist-editor-header {
			align-items: flex-start;
			flex-direction: column;
		}
		.checklist-editor-header > div:last-child {
			width: 100%;
			flex-wrap: nowrap;
			justify-content: space-between;
		}
		.checklist-items-header { display: none; }
		.checklist-item-row {
			grid-template-columns: 3rem minmax(0, 1fr);
			row-gap: 0.75rem;
		}
		.item-number-wrap {
			align-self: start;
		}
		.item-actions {
			display: flex;
			grid-column: 1 / -1;
			align-items: center;
			justify-content: space-between;
			gap: 0.5rem;
			padding-left: 3rem;
		}
		.media-action {
			gap: 0.25rem;
		}
		.mobile-media-label {
			display: inline;
			max-width: 5.5rem;
			font-size: 0.75rem;
			line-height: 1.1;
		}
	}
</style>
